import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

const Ctx = createContext(() => {});
export const useLightbox = () => useContext(Ctx);

const clamp = (n) => Math.min(4, Math.max(1, n));

export const LightboxProvider = ({ children }) => {
  const [img, setImg] = useState(null);
  const [scale, setScale] = useState(1);
  const pinch = useRef(null);

  const open = useCallback((src, alt = "") => { setImg({ src, alt }); setScale(1); }, []);
  const close = () => setImg(null);
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => { if (e.touches.length === 2) pinch.current = { d: dist(e.touches), s: scale }; };
  const onTouchMove = (e) => { if (e.touches.length === 2 && pinch.current) setScale(clamp(pinch.current.s * (dist(e.touches) / pinch.current.d))); };
  const onTouchEnd = () => { pinch.current = null; };

  useEffect(() => {
    if (!img) { window.__lenis?.start(); return; }
    window.__lenis?.stop();
    const onKey = (e) => { if (e.key === "Escape") close(); if (e.key === "+") setScale((s) => clamp(s + 0.5)); if (e.key === "-") setScale((s) => clamp(s - 0.5)); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [img]);

  return (
    <Ctx.Provider value={open}>
      {children}
      <AnimatePresence>
        {img && (
          <motion.div
            data-testid="lightbox" role="dialog" aria-modal="true"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[100] bg-night/95 backdrop-blur-xl flex items-center justify-center overflow-hidden touch-none"
            onClick={close}
            onWheel={(e) => setScale((s) => clamp(s - e.deltaY * 0.002))}
            onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
          >
            <div className="absolute inset-0 grain pointer-events-none" />
            <motion.img
              key={img.src} src={img.src} alt={img.alt} data-testid="lightbox-image"
              initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale }} exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.25 } }}
              transition={{ type: "spring", stiffness: 160, damping: 24 }}
              drag={scale > 1} dragMomentum={false}
              onClick={(e) => { e.stopPropagation(); setScale((s) => (s > 1 ? 1 : 2.2)); }}
              className={`max-h-[88vh] max-w-[92vw] object-contain select-none shadow-[0_0_120px_rgba(205,156,32,0.25)] ${scale > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
              draggable={false}
            />
            <div className="absolute top-4 right-4 sm:top-5 sm:right-5 z-20 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {[
                ["lightbox-zoom-out", ZoomOut, () => setScale((s) => clamp(s - 0.5))],
                ["lightbox-zoom-reset", Maximize2, () => setScale(1)],
                ["lightbox-zoom-in", ZoomIn, () => setScale((s) => clamp(s + 0.5))],
                ["lightbox-close", X, close],
              ].map(([id, Icon, fn]) => (
                <button key={id} data-testid={id} onClick={fn} aria-label={id} className="h-11 w-11 border border-gold/40 bg-night/70 text-saffron hover:bg-gold/15 hover:border-saffron transition-colors flex items-center justify-center">
                  <Icon size={18} strokeWidth={1.6} />
                </button>
              ))}
            </div>
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 w-[92vw] text-center text-[10px] uppercase tracking-[0.3em] text-ivory/50" data-testid="lightbox-caption">
              {img.alt} · {Math.round(scale * 100)}% · tap or click to zoom, pinch or scroll to adjust
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </Ctx.Provider>
  );
};

export const ZoomImage = ({ src, alt, className = "", shine = false, ...rest }) => {
  const open = useLightbox();
  return (
    <img
      src={src} alt={alt} loading="lazy"
      data-testid={rest["data-testid"] || `zoom-image-${src.split("/").pop().split(".")[0]}`}
      onClick={(e) => { e.stopPropagation(); open(src, alt); }}
      className={`cursor-zoom-in ${className}`}
      {...rest}
    />
  );
};
